import {getLinkData} from 'Application/utils/linkFieldUtils';

import type {LinkField} from 'types/payload-types';

/**
 * A custom hook that extracts and normalizes link data for the `PartnerCard` component.
 * This hook processes both the hotline link and the call-to-action (CTA) button link
 * by using the `getLinkData` utility. It resolves the relevant attributes into a
 * consistent format so that they can be safely used in rendering the card.
 * For the hotline, only `href` and `label` are returned. For the CTA button,
 * additional attributes like `rel` and `target` are included to support
 * external or downloadable links.
 * If either the hotline or button is not provided, their respective values will be `undefined`,
 * ensuring safe conditional rendering in the component.
 *
 * @param hotline An optional link configuration for the hotline, typically representing a phone number.
 * @param button  An optional link configuration for the call-to-action button.
 * @returns An object containing:
 * - `href`: The resolved URL for the hotline link.
 * - `label`: The display label for the hotline link.
 * - `buttonHref`: The resolved URL for the CTA button.
 * - `buttonLabel`: The display label for the CTA button.
 * - `rel`: The `rel` attribute for the CTA button link (if applicable).
 * - `target`: The `target` attribute for the CTA button link (e.g., `_blank` for external links).
 *
 * @example
 * ```tsx
 * const { href, label, buttonHref, buttonLabel } = usePartnerCard(
 *   { label: "0800 987 654", linkType: "phone", url: "tel:0800987654" },
 *   { label: "Jetzt Partner werden", linkType: "internal", internal: "/partners/signup" }
 * );
 *
 * // href: "tel:0800987654"
 * // label: "0800 987 654"
 * // buttonHref: "/partners/signup"
 * // buttonLabel: "Jetzt Partner werden"
 * ```
 */
export const usePartnerCard = (hotline?: LinkField, button?: LinkField) => {
    const {href, label} = getLinkData(hotline);
    const {href: buttonHref, label: buttonLabel, rel, target} = getLinkData(button);

    return {
        buttonHref,
        buttonLabel,
        href,
        label,
        rel,
        target
    };
};